import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    const path = location?.pathname;
    if (['/public-claim-search', '/claim-detail-analysis', '/narrative-explorer']?.includes(path)) {
      return 'verify';
    } else if (['/expert-console-dashboard', '/fact-check-workbench', '/crisis-response-center']?.includes(path)) {
      return 'expert';
    } else if (['/platform-bridge-interface', '/research-data-portal']?.includes(path)) {
      return 'institutional';
    }
    return 'verify';
  });

  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const navigationTabs = [
    {
      id: 'verify',
      label: 'Verify Claims',
      description: 'Public fact-checking and claim verification',
      routes: [
        { path: '/public-claim-search', label: 'Claim Search' },
        { path: '/claim-detail-analysis', label: 'Detail Analysis' },
        { path: '/narrative-explorer', label: 'Narrative Explorer' }
      ]
    },
    {
      id: 'expert',
      label: 'Expert Console',
      description: 'Professional fact-checking workspace',
      routes: [
        { path: '/expert-console-dashboard', label: 'Dashboard' },
        { path: '/fact-check-workbench', label: 'Workbench' },
        { path: '/crisis-response-center', label: 'Crisis Response' }
      ]
    },
    {
      id: 'institutional',
      label: 'Institutional Access',
      description: 'Research and platform management',
      routes: [
        { path: '/platform-bridge-interface', label: 'Platform Bridge' },
        { path: '/research-data-portal', label: 'Research Portal' }
      ]
    }
  ];

  const moreMenuItems = [
    { label: 'Settings', icon: 'Settings', path: '/settings' },
    { label: 'Help & Support', icon: 'HelpCircle', path: '/help' },
    { label: 'Admin Panel', icon: 'Shield', path: '/admin' }
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    const tab = navigationTabs?.find(t => t?.id === tabId);
    if (tab && tab?.routes?.length > 0) {
      window.location.href = tab?.routes?.[0]?.path;
    }
  };

  const handleMoreMenuToggle = () => {
    setShowMoreMenu(!showMoreMenu);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-soft">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo Section */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
            <Icon name="Shield" size={24} color="white" strokeWidth={2} />
          </div>
          <div className="flex flex-col">
            <h1 className="text-lg font-semibold text-foreground leading-tight">
              TruthGuard EU
            </h1>
            <span className="text-xs text-muted-foreground leading-tight">
              European Fact-Checking Network
            </span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <nav className="hidden md:flex items-center space-x-1">
          {navigationTabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => handleTabClick(tab?.id)}
              className={`
                relative px-4 py-2 rounded-lg text-sm font-medium transition-smooth
                ${activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground shadow-soft'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              {tab?.label}
              {activeTab === tab?.id && (
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary rounded-full" />
              )}
            </button>
          ))}
        </nav>

        {/* Right Section */}
        <div className="flex items-center space-x-3">
          {/* Real-time Status Indicator */}
          <div className="hidden lg:flex items-center space-x-2 px-3 py-1.5 bg-success/10 rounded-full">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse-gentle" />
            <span className="text-xs font-medium text-success">System Active</span>
          </div>

          {/* Quick Search */}
          <Button
            variant="outline"
            size="sm"
            iconName="Search"
            iconPosition="left"
            className="hidden sm:flex"
            onClick={() => {
              // Quick search functionality
            }}
          >
            Quick Search
          </Button>

          {/* More Menu */}
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              iconName="MoreHorizontal"
              onClick={handleMoreMenuToggle}
            />
            
            {showMoreMenu && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-lg shadow-elevated z-50">
                <div className="py-1">
                  {moreMenuItems?.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setShowMoreMenu(false);
                        window.location.href = item?.path;
                      }}
                      className="flex items-center w-full px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                    >
                      <Icon name={item?.icon} size={16} className="mr-3" />
                      {item?.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="icon"
            iconName="Menu"
            className="md:hidden"
            onClick={() => {
              // Mobile menu toggle
            }}
          />
        </div>
      </div>
      {/* Mobile Navigation */}
      <div className="md:hidden border-t border-border bg-card">
        <nav className="flex">
          {navigationTabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => handleTabClick(tab?.id)}
              className={`
                flex-1 px-3 py-3 text-xs font-medium text-center transition-smooth
                ${activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              {tab?.label}
            </button>
          ))}
        </nav>
      </div>
      {/* Overlay for mobile menu */}
      {showMoreMenu && (
        <div
          className="fixed inset-0 z-40 bg-black/20"
          onClick={() => setShowMoreMenu(false)}
        />
      )}
    </header>
  );
};

export default Header;